import React, { useCallback, useEffect, useState } from 'react';
import api, { getDiscordAuthorizeUrl, getTwitchAuthorizeUrl } from '../api';
import { AUTH_MESSAGE, useTwitchAuth } from '../context/TwitchAuthContext';

function openPopup(url: string, name: string) {
  const w = 520;
  const h = 720;
  const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
  const popupName = `${name}-${Date.now()}`;
  const popup = window.open(
    url,
    popupName,
    `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`,
  );
  if (!popup) {
    window.location.href = url;
    return null;
  }
  return popup;
}

interface DualLinkModalProps {
  open: boolean;
  onClose: () => void;
}

function LinkStep({
  done,
  step,
  label,
  sub,
  disabled,
  onClick,
}: {
  done: boolean;
  step: string;
  label: string;
  sub: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || done}
      className={[
        'flex flex-col items-start gap-2 p-4 rounded-xl border text-left transition-all',
        done
          ? 'border-emerald-400/50 bg-emerald-400/10 cursor-default'
          : 'border-white/10 bg-rose-400/5 hover:border-rose-400/40 cursor-pointer',
        disabled && !done ? 'opacity-50 cursor-not-allowed' : '',
      ].join(' ')}
    >
      <div className="flex items-center gap-2 text-sm font-medium tracking-wide">
        <span
          className={[
            'w-[18px] h-[18px] rounded flex items-center justify-center text-[0.7rem] font-bold',
            done ? 'bg-emerald-400 text-[#0d0b1e] border-2 border-emerald-400' : 'border-2 border-white/35',
          ].join(' ')}
        >
          {done ? '✓' : ''}
        </span>
        <span>{step}</span>
      </div>
      <span className="text-xs text-slate-400 break-all">{sub || label}</span>
    </button>
  );
}

const DualLinkModal: React.FC<DualLinkModalProps> = ({ open, onClose }) => {
  const { user, token, setSession } = useTwitchAuth();
  const [twitchOk, setTwitchOk] = useState(false);
  const [discordOk, setDiscordOk] = useState(false);
  const [twitchLabel, setTwitchLabel] = useState('');
  const [discordLabel, setDiscordLabel] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!localStorage.getItem('eugenial_twitch_token')) {
      setTwitchOk(false);
      setDiscordOk(false);
      return;
    }
    try {
      const { data } = await api.get('/discord/auth/status');
      setTwitchOk(!!data.twitchLinked);
      setDiscordOk(!!data.discordLinked);
      if (data.twitch?.displayName) setTwitchLabel(data.twitch.displayName);
      if (data.discord) {
        setDiscordLabel(data.discord.displayName || data.discord.username || '');
      }
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setError('');
    if (user || token) {
      setTwitchOk(true);
      setTwitchLabel(user?.displayName || '');
      refreshStatus();
    } else {
      setTwitchOk(false);
      setDiscordOk(false);
      setTwitchLabel('');
      setDiscordLabel('');
    }
  }, [open, user, token, refreshStatus]);

  useEffect(() => {
    if (!open) return;
    const onMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === AUTH_MESSAGE && event.data.token && event.data.user) {
        setSession(event.data.token, event.data.user);
        setTwitchOk(true);
        setTwitchLabel(event.data.user.displayName || event.data.user.login);
        setTimeout(() => refreshStatus(), 300);
      }
      if (event.data?.type === 'EUGENIAL_DISCORD_LINK') {
        if (event.data.ok) {
          setDiscordOk(true);
          setDiscordLabel(event.data.discord?.displayName || event.data.discord?.username || 'Discord');
          setError('');
          refreshStatus();
        } else if (event.data.error) {
          setError(event.data.error);
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [open, setSession, refreshStatus]);

  if (!open) return null;

  const connectTwitch = () => {
    setError('');
    try {
      window.location.assign(getTwitchAuthorizeUrl());
    } catch (e: unknown) {
      setError((e as Error).message || 'Error al abrir Twitch');
    }
  };

  const connectDiscord = async () => {
    setError('');
    if (!localStorage.getItem('eugenial_twitch_token')) {
      setError('Primero conectá Twitch (paso 1).');
      return;
    }
    setBusy(true);
    try {
      openPopup(getDiscordAuthorizeUrl(), 'eugenial-discord-login');
    } catch (e: unknown) {
      setError((e as Error).message || 'Error al abrir Discord');
    } finally {
      setBusy(false);
    }
  };

  const complete = twitchOk && discordOk;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-[#05030e]/80"
      onClick={(e) => { if (e.target === e.currentTarget && complete) onClose(); }}
      role="presentation"
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-gradient-to-b from-[#1a1333] to-[#0d0b1e] p-6 shadow-2xl shadow-rose-900/20">
        <h2 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-300 to-sky-300 mb-1">
          Vincular cuentas
        </h2>
        <p className="text-sm text-slate-400 mb-5 leading-relaxed">
          Conectá Twitch y Discord. Cuando ambos tengan el check, ya podés usar el sitio.
          Los nombres se actualizan solos si los cambiás.
        </p>

        {error && <p className="text-sm text-rose-400 mb-3">{error}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <LinkStep
            done={twitchOk}
            step="1 · Twitch"
            label="Conectar Twitch"
            sub={twitchOk ? twitchLabel || 'Conectado' : 'Conectar Twitch'}
            onClick={connectTwitch}
          />
          <LinkStep
            done={discordOk}
            step="2 · Discord"
            label="Conectar Discord"
            sub={
              !twitchOk
                ? 'Primero Twitch'
                : discordOk
                  ? discordLabel || 'Vinculado'
                  : 'Conectar Discord'
            }
            disabled={!twitchOk || discordOk || busy}
            onClick={connectDiscord}
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm border border-white/15 text-slate-300 hover:text-white"
          >
            {complete ? 'Listo' : 'Cerrar'}
          </button>
          {complete && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-rose-400/90 to-pink-500/90 text-white"
            >
              Continuar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DualLinkModal;
